import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAxios } from "hooks/useAxios";
import { USER_API } from "config";
import { workspaceKeys } from "queries/workspace.keys";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace/useWorkspaceIdentifiers";
import { normalizeId } from "utils";
import { canWorkspace, type WorkspacePermission } from "utils/permissions/permissionConstants";
import { resolvePermissionsFromRole } from "./useWorkspaceAuth.utils";
import { useSessionUser, useUserCatalog } from "hooks/useSession";

/**
 * Workspace user type following WorkOS-style pattern
 */
export interface WorkspaceUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  permissions: string[];
  workspaceId: string;
  role: string | null;
}

/**
 * Hook result type
 */
export interface UseWorkspaceAuthResult {
  user: WorkspaceUser | null;
  canWorkspace: (permission: WorkspacePermission) => boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<any>;
}

/**
 * Hook for workspace authorization following WorkOS AuthKit pattern
 * Uses the /me endpoint to get user data and workspace-specific permissions
 *
 * Provides two ways to check permissions:
 * 1. WorkOS-style: `user.permissions.includes(Permissions.WORKSPACE_MEMBER_INVITE)`
 * 2. Convenience function: `canWorkspace(Permissions.WORKSPACE_MEMBER_INVITE)` (recommended)
 *
 * @example
 * ```tsx
 * // Using canWorkspace() function (recommended - handles edge cases, type-safe)
 * const { canWorkspace } = useWorkspaceAuth();
 * import { Permissions } from "utils/permissions/permissionConstants";
 *
 * if (!canWorkspace(Permissions.WORKSPACE_MEMBER_INVITE)) {
 *   return <p>You do not have permission to manage members.</p>;
 * }
 *
 * return (
 *   <div>
 *     {canWorkspace(Permissions.WORKSPACE_BILLING_UPDATE) && (
 *       <button>Manage Billing</button>
 *     )}
 *   </div>
 * );
 * ```
 *
 * @example
 * ```tsx
 * // Using user object (WorkOS-style - explicit)
 * const { user } = useWorkspaceAuth();
 * import { Permissions } from "utils/permissions/permissionConstants";
 *
 * if (!user || !user.permissions.includes(Permissions.WORKSPACE_MEMBER_INVITE)) {
 *   return <p>You do not have permission to manage members.</p>;
 * }
 *
 * return (
 *   <div>
 *     {user.permissions.includes(Permissions.WORKSPACE_BILLING_UPDATE) && (
 *       <button>Manage Billing</button>
 *     )}
 *   </div>
 * );
 * ```
 */
export const useWorkspaceAuth = (workspaceId?: string | number | null): UseWorkspaceAuthResult => {
  const axios = useAxios({ allResponse: true });
  const { workspaceId: urlWorkspaceId } = useWorkspaceIdentifiers();
  const targetWorkspaceId = workspaceId || urlWorkspaceId;
  const {
    data: session,
    isLoading: sessionLoading,
    isError: sessionError,
    error: sessionErr,
    refetch: refetchSession,
  } = useSessionUser();
  const { data: catalog } = useUserCatalog();

  // Fetch workspace-specific permissions if workspaceId is available
  // Use the same query key as useWorkspaceSettings to share cache and avoid duplicate requests
  const normalizedWorkspaceId = targetWorkspaceId ? normalizeId(targetWorkspaceId) : "";
  const {
    data: workspaceData,
    isLoading: workspaceLoading,
    isError: workspaceError,
    error: workspaceErr,
    refetch: workspaceRefetch,
  } = useQuery({
    queryKey: workspaceKeys.detail(normalizedWorkspaceId),
    queryFn: async () => {
      if (!targetWorkspaceId) return null;
      const normalizedId = normalizeId(targetWorkspaceId);
      const response = await axios.get(USER_API.getWorkspace(normalizedId));
      return response.data.data || response.data;
    },
    enabled: !!targetWorkspaceId && normalizedWorkspaceId !== "",
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false, // Don't refetch on mount - use cached data if available
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 1,
  });

  // Transform data into WorkOS-style user object
  const user = useMemo<WorkspaceUser | null>(() => {
    if (!session) return null;
    const normalizedWorkspaceId = targetWorkspaceId ? String(normalizeId(targetWorkspaceId)) : "";

    // Extract workspace-specific permissions from workspace data
    let permissions: string[] = [];
    let role: string | null = null;

    if (workspaceData) {
      // Check if workspace has embedded permissions
      if (workspaceData.permissions && Array.isArray(workspaceData.permissions.permissions)) {
        permissions = workspaceData.permissions.permissions;
        role = workspaceData.permissions.role || null;
      } else {
        const workspace = catalog?.workspaces.find(
          (candidate) => String(candidate.workspace_id) === normalizedWorkspaceId
        );
        if (workspace) {
          role = workspace.role || null;
          permissions = resolvePermissionsFromRole(role);
        }
      }
    }

    return {
      id: session.user_id,
      email: session.email,
      firstName: session.first_name,
      lastName: session.last_name,
      permissions,
      workspaceId: normalizedWorkspaceId,
      role,
    };
  }, [catalog?.workspaces, session, workspaceData, targetWorkspaceId]);

  const isLoading = sessionLoading || workspaceLoading;

  // Scoped permission checking - TypeScript enforces workspace permissions only
  const canWorkspacePermission = useCallback(
    (permission: WorkspacePermission): boolean => {
      // Return false during loading or if user/permissions are not available
      if (isLoading || !user || !user.permissions) return false;
      return canWorkspace(permission, user.permissions);
    },
    [user, isLoading]
  );
  const isError = sessionError || workspaceError;
  const error = (sessionErr || workspaceErr) as Error | null;

  const refetch = async () => {
    await Promise.all([refetchSession(), workspaceRefetch()]);
  };

  return {
    user,
    canWorkspace: canWorkspacePermission,
    isLoading,
    isError,
    error,
    refetch,
  };
};
